import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { FcDashboardClient } from '@/infrastructure/FcDashboardClient';
import type { FcReportSnapshot } from '@/models/FcDashboard';
import { instantMs, kstMinute } from '@/utils/fulfillmentTime';
import { ClockIcon, RefreshIcon, WarningIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { REFRESH_MS, waitedLabel } from './TodayTab';
import { useFcdLang, useFcdText, type FcdLang } from './fcdText';

/**
 * The report runs every twenty minutes from 08:00 to the 20:00 close, Seoul time. Outside those hours the latest copy
 * is the evening's close, not a run that failed to come, so the staleness band is shown only inside them.
 */
const REPORT_HOURS_KST = { from: 8, to: 21 } as const;

/** Six runs missed in a row before the tab says so: one or two late cycles are the runner's own business. */
const STALE_AFTER_MS = 2 * 60 * 60 * 1000;

/** How often the band's age label is brought forward while the tab sits open. */
const CLOCK_TICK_MS = 60 * 1000;

/**
 * The report's sections and blocks, in its order and with its own names: the empty state before the first copy arrives
 * shows them, so the tab already says what it will hold.
 */
export const REPORT_GROUPS: Record<FcdLang, ReadonlyArray<{ title: string; blocks: readonly string[] }>> = {
  ko: [
    { title: '오더팀', blocks: ['어제까지 미도착 제품 (48시간+)', '제품은 다 왔는데 입고가 안 끝남'] },
    {
      title: '물류센터',
      blocks: [
        '입고 끝 — 포장만 남음 (48시간+)',
        '포장 완료 — 출고 대기',
        '어제 입고 (제품) · 아웃바운드 (주문) · 인계',
        '출고 완료 기록',
      ],
    },
    { title: '주문→고객 도착', blocks: ['국가별 소요일과 배송 중 멈춘 오더'] },
    { title: 'SLA 비교', blocks: ['경쟁사 SLA 와 우리 실측', '운송사 약속과 실측'] },
  ],
  en: [
    { title: 'Order Team', blocks: ['Not arrived by yesterday (48h+)', 'All items arrived but not Inbounded'] },
    {
      title: 'Fulfillment Center',
      blocks: [
        'Inbounded — packing only (48h+)',
        'Packed — waiting to ship',
        'Yesterday: Inbound (units) · Outbound (orders) · Handover',
        'Handover record',
      ],
    },
    { title: 'Order → Customer', blocks: ['Days by country and orders stuck in transit'] },
    { title: 'SLA Comparison', blocks: ['Competitor SLAs and our measured time', 'Carrier promise and measured time'] },
  ],
};

/** The Seoul hour of an instant, 0 to 23. */
const kstHourOf = (at: Date): number =>
  Number(new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Seoul', hour: 'numeric', hour12: false }).format(at)) % 24;

/** Whether a run should be arriving every twenty minutes at this instant. */
export const isReportHoursKst = (at: Date): boolean => {
  const hour = kstHourOf(at);
  return hour >= REPORT_HOURS_KST.from && hour < REPORT_HOURS_KST.to;
};

/** A run older than two hours during report hours: the copy is still shown, with a band saying how old it is. */
export const isStaleRun = (runAt: string, now: Date): boolean => {
  const run = instantMs(runAt);
  return run !== null && isReportHoursKst(now) && now.getTime() - run > STALE_AFTER_MS;
};

/** The frame's address: the signed link plus the admin's language, which the report page reads when it loads. */
export const frameSrcOf = (url: string, lang: FcdLang): string => `${url}#lang=${lang}`;

/** When a link this close to running out is all the tab holds, it asks for a fresh one before opening the frame again. */
const URL_MARGIN_MS = 2 * 60 * 1000;

/**
 * The copy the frame shows. The link is brought forward on every read so a later remount never opens an expired one,
 * but the frame itself is only remounted when a copy with a new hash arrives, the reader asks for a reload, or the
 * language changes: a mounted frame keeps the page it opened with.
 */
interface Frame {
  sha256: string;
  url: string;
  urlExpiresAt: string;
  /** Counts reloads of the same copy, so asking again remounts the frame even when nothing changed. */
  reload: number;
}

/**
 * The frame opens the address it was mounted with and ignores later changes to it: a fresher link for the same copy
 * must not navigate a page the reader is looking at. A new address is taken by remounting (the parent's `key`).
 */
function ReportFrame({ src, title }: { src: string; title: string }) {
  const [opened] = useState(src);
  return (
    <iframe
      className="fcd-report-frame"
      title={title}
      src={opened}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
    />
  );
}

/**
 * The Fulfillment Report tab (SSD-7638): the latest copy of the Kali-server report, as the team sees it, in a sandboxed
 * frame under a bar with the run's time and kind. The copy comes from the company bucket through a signed link, so a
 * report server that stops leaves the last copy standing with its time.
 */
export function ReportTab() {
  const text = useFcdText();
  const lang = useFcdLang();
  const client = useMemo(() => new FcDashboardClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  // undefined until the first read answers; null when the server has no copy yet.
  const [snapshot, setSnapshot] = useState<FcReportSnapshot | null | undefined>(undefined);
  const [frame, setFrame] = useState<Frame | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const requestRef = useRef(0);
  const lastReadRef = useRef(0);

  const load = useCallback(
    async (reload = false) => {
      const request = ++requestRef.current;
      setReading(true);
      try {
        const next = await client.getReportSnapshot();
        if (request !== requestRef.current) {
          return;
        }
        lastReadRef.current = Date.now();
        setNow(new Date());
        setSnapshot(next);
        setFailure(null);
        setFrame((current) => {
          if (!next) {
            return null;
          }
          const reloads = (current?.reload ?? 0) + (reload ? 1 : 0);
          return { sha256: next.sha256, url: next.url, urlExpiresAt: next.urlExpiresAt, reload: reloads };
        });
      } catch (error) {
        if (request === requestRef.current) {
          setFailure(error instanceof Error ? error.message : String(error));
        }
      } finally {
        if (request === requestRef.current) {
          setReading(false);
        }
      }
    },
    [client],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Every quarter of an hour while the tab is in view; a tab coming back after longer than that reads at once.
  useEffect(() => {
    const visible = () => document.visibilityState === 'visible';
    const timer = window.setInterval(() => {
      if (visible()) {
        void load();
      }
    }, REFRESH_MS);
    const onVisibility = () => {
      if (visible() && Date.now() - lastReadRef.current >= REFRESH_MS) {
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  // The age in the band keeps moving while the tab sits open.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => window.clearInterval(tick);
  }, []);

  // A language change remounts the frame; when the link it holds is about to run out, a fresh one is read first.
  const frameRef = useRef(frame);
  frameRef.current = frame;
  const firstLangRef = useRef(true);
  useEffect(() => {
    if (firstLangRef.current) {
      firstLangRef.current = false;
      return;
    }
    const held = frameRef.current;
    if (held && (instantMs(held.urlExpiresAt) ?? 0) - Date.now() < URL_MARGIN_MS) {
      void load(true);
    }
  }, [lang, load]);

  const stale = snapshot ? isStaleRun(snapshot.runAt, now) : false;

  return (
    <div className="fcd-report">
      {snapshot && (
        <div className="fcd-bar fcd-report-bar" role="group" aria-label={text.reportBar}>
          <span className={`fcd-report-kind is-${snapshot.runKind}`}>{text.reportKind[snapshot.runKind]}</span>
          <span className="fcd-meta">
            <ClockIcon />
            <span>
              <b>{text.reportRunAt(kstMinute(snapshot.runAt))}</b> ·{' '}
              {text.reportReceived(kstMinute(snapshot.receivedAt))}
            </span>
          </span>
          <span className="sp" />
          <button type="button" className="btn btn-blue" disabled={reading} onClick={() => void load(true)}>
            <RefreshIcon />
            {reading ? text.reading : text.refresh}
          </button>
        </div>
      )}
      {stale && snapshot && (
        <div className="fcd-state" role="status">
          <WarningIcon />
          <span>{text.reportStale(waitedLabel(snapshot.runAt, now.toISOString(), lang))}</span>
        </div>
      )}
      {failure && (
        <div className="fcd-state is-error" role="alert">
          <WarningIcon />
          <span>
            {text.reportFailed}: {failure}
          </span>
          {!snapshot && (
            <button type="button" className="btn btn-line" disabled={reading} onClick={() => void load(true)}>
              {text.refresh}
            </button>
          )}
        </div>
      )}
      {snapshot === undefined && !failure && (
        <div className="fcd-state" role="status">
          <ClockIcon />
          <span>{text.reportReading}</span>
        </div>
      )}
      {snapshot === null && (
        <>
          <div className="fcd-state" role="status">
            <ClockIcon />
            <span>{text.reportEmpty}</span>
          </div>
          <section className="fcd-report-map" aria-label={text.logisticsList}>
            {REPORT_GROUPS[lang].map((group, index) => (
              <section key={group.title} className="fcd-report-group" aria-label={group.title}>
                <div className="fcd-report-head">
                  <span className="fcd-step">{index + 1}</span>
                  <h3>{group.title}</h3>
                </div>
                <ul>
                  {group.blocks.map((block) => (
                    <li key={block} className="fcd-report-block">
                      <span>{block}</span>
                      <span className="fcd-wip">{text.wip}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </section>
        </>
      )}
      {snapshot && frame && (
        <>
          <p className="fcd-report-note">{text.reportCopyNote}</p>
          <ReportFrame
            key={`${frame.sha256}|${frame.reload}|${lang}`}
            src={frameSrcOf(frame.url, lang)}
            title={text.reportFrame}
          />
        </>
      )}
    </div>
  );
}
